import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import FontFamily from '@tiptap/extension-font-family'
import { TextStyle } from '@tiptap/extension-text-style'
import Placeholder from '@tiptap/extension-placeholder'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import clsx from 'clsx'
import './styles.css'

const FONTS = [
	{ label: 'Default', value: '' },
	{ label: 'Arial', value: 'Arial' },
	{ label: 'Georgia', value: 'Georgia' },
	{ label: 'Roboto', value: 'Roboto' },
	{ label: 'Times New Roman', value: '"Times New Roman"' },
	{ label: 'Courier New', value: '"Courier New"' },
]

interface MessageRichEditorProps {
	value: string
	onChange: (html: string) => void
	placeholder?: string
	disabled?: boolean
	/** Minimum height of the content area in pixels. Defaults to 120. */
	minHeight?: number
	/** Show a red error border around the editor. */
	error?: boolean
	className?: string
}

function MessageRichEditor({
	value,
	onChange,
	placeholder,
	disabled = false,
	minHeight = 120,
	error = false,
	className,
}: MessageRichEditorProps) {
	const [showEmojiPicker, setShowEmojiPicker] = useState(false)
	const [showFontMenu, setShowFontMenu] = useState(false)
	const [emojiPickerPos, setEmojiPickerPos] = useState<{ top: number; right: number } | null>(null)
	const emojiPickerRef = useRef<HTMLDivElement>(null)
	const emojiButtonRef = useRef<HTMLButtonElement>(null)
	const fontMenuRef = useRef<HTMLDivElement>(null)

	const lastPushedValue = useRef(value)

	const editor = useEditor({
		extensions: [
			StarterKit,
			Underline,
			TextStyle,
			FontFamily,
			Placeholder.configure({ placeholder: placeholder ?? '' }),
		],
		content: value,
		editable: !disabled,
		onUpdate({ editor: e }) {
			const html = e.getHTML()
			lastPushedValue.current = html
			onChange(html)
		},
	})

	// Sync external value changes (e.g., draft re-seed when modal re-opens)
	useEffect(() => {
		if (!editor || value === lastPushedValue.current) return
		lastPushedValue.current = value
		editor.commands.setContent(value || '')
	}, [value, editor])

	// Close dropdowns on outside click
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (showEmojiPicker && emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
				setShowEmojiPicker(false)
			}
			if (showFontMenu && fontMenuRef.current && !fontMenuRef.current.contains(e.target as Node)) {
				setShowFontMenu(false)
			}
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [showEmojiPicker, showFontMenu])

	const handleToggleEmojiPicker = () => {
		if (!showEmojiPicker && emojiButtonRef.current) {
			const rect = emojiButtonRef.current.getBoundingClientRect()
			setEmojiPickerPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
		}
		setShowEmojiPicker(p => !p)
	}

	const handleEmojiClick = (emojiData: EmojiClickData) => {
		editor?.chain().focus().insertContent(emojiData.emoji).run()
		setShowEmojiPicker(false)
	}

	const currentFont = editor?.getAttributes('textStyle').fontFamily ?? ''
	const currentFontLabel = FONTS.find(f => f.value === currentFont)?.label ?? 'Default'

	return (
		<div
			className={clsx(
				'message-rte-wrapper relative rounded-lg border-2',
				error ? 'border-secondary-red' : 'border-white/[0.08]',
				className
			)}
		>
			{/* Toolbar — rounded top to match wrapper; no overflow-hidden so dropdowns escape */}
			<div className="flex items-center gap-0.5 px-2 h-[34px] bg-primary-surface/40 border-b border-white/[0.06] rounded-t-lg">
				{/* Font picker */}
				<div className="relative shrink-0" ref={fontMenuRef}>
					<button
						type="button"
						onClick={() => setShowFontMenu(p => !p)}
						disabled={disabled}
						className="flex items-center gap-1.5 bg-white/[0.07] hover:bg-white/[0.12] text-secondary-text text-xs rounded px-2 h-[22px] transition-colors duration-150 max-w-[100px]"
					>
						<span className="truncate">{currentFontLabel}</span>
						<svg
							className="shrink-0 w-3 h-3 opacity-60"
							fill="none"
							viewBox="0 0 12 12"
							stroke="currentColor"
							strokeWidth={1.5}
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5l3 3 3-3" />
						</svg>
					</button>

					{showFontMenu && (
						<div className="absolute top-full left-0 mt-1 bg-[#3d3e4d] border border-white/[0.1] rounded-lg py-1 z-50 min-w-[130px] shadow-lg">
							{FONTS.map(f => (
								<button
									key={f.value}
									type="button"
									onClick={() => {
										if (f.value) {
											editor?.chain().focus().setFontFamily(f.value).run()
										} else {
											editor?.chain().focus().unsetFontFamily().run()
										}
										setShowFontMenu(false)
									}}
									className={clsx(
										'w-full text-left px-3 py-1.5 text-xs transition-colors duration-100 hover:bg-white/10',
										currentFont === f.value ? 'text-primary-text' : 'text-secondary-text'
									)}
									style={f.value ? { fontFamily: f.value } : undefined}
								>
									{f.label}
								</button>
							))}
						</div>
					)}
				</div>

				<div className="w-px h-4 bg-white/10 mx-1.5 shrink-0" />

				{/* Bold */}
				<button
					type="button"
					onClick={() => editor?.chain().focus().toggleBold().run()}
					disabled={disabled}
					className={clsx(
						'w-[26px] h-[26px] flex items-center justify-center rounded text-sm font-bold transition-colors duration-150',
						editor?.isActive('bold')
							? 'bg-white/15 text-primary-text'
							: 'text-secondary-text hover:bg-white/10 hover:text-primary-text'
					)}
				>
					B
				</button>

				{/* Italic */}
				<button
					type="button"
					onClick={() => editor?.chain().focus().toggleItalic().run()}
					disabled={disabled}
					className={clsx(
						'w-[26px] h-[26px] flex items-center justify-center rounded text-sm italic transition-colors duration-150',
						editor?.isActive('italic')
							? 'bg-white/15 text-primary-text'
							: 'text-secondary-text hover:bg-white/10 hover:text-primary-text'
					)}
				>
					I
				</button>

				{/* Underline */}
				<button
					type="button"
					onClick={() => editor?.chain().focus().toggleUnderline().run()}
					disabled={disabled}
					className={clsx(
						'w-[26px] h-[26px] flex items-center justify-center rounded text-sm underline transition-colors duration-150',
						editor?.isActive('underline')
							? 'bg-white/15 text-primary-text'
							: 'text-secondary-text hover:bg-white/10 hover:text-primary-text'
					)}
				>
					U
				</button>

				<div className="flex-1" />

				{/* Emoji */}
				<button
					ref={emojiButtonRef}
					type="button"
					onClick={handleToggleEmojiPicker}
					disabled={disabled}
					title="Add emoji"
					className="w-[26px] h-[26px] flex items-center justify-center rounded text-sm transition-colors duration-150 hover:bg-white/10"
				>
					😀
				</button>
			</div>

			{/* Content area — rounded bottom to match wrapper */}
			<div className="bg-white/[0.04] overflow-auto text-primary-text text-left rounded-b-lg" style={{ minHeight }}>
				<EditorContent editor={editor} />
			</div>

			{/* Emoji picker — portalled to body so it escapes all modal overflow/scroll containers */}
			{showEmojiPicker && emojiPickerPos && createPortal(
				<div
					ref={emojiPickerRef}
					className="fixed z-[9999]"
					style={{ top: emojiPickerPos.top, right: emojiPickerPos.right }}
				>
					<EmojiPicker
						onEmojiClick={handleEmojiClick}
						width={260}
						height={300}
						previewConfig={{ showPreview: false }}
						style={
							{
								'--epr-emoji-size': '22px',
								'--epr-category-navigation-button-size': '18px',
								'--epr-search-input-height': '32px',
								'--epr-header-padding': '6px 8px',
							} as React.CSSProperties
						}
					/>
				</div>,
				document.body
			)}
		</div>
	)
}

export { MessageRichEditor }
export type { MessageRichEditorProps }
